/**
 * Asks for one second factor (#43): a code from an authenticator app, a passkey, or a
 * recovery code. Used for the second sign-in step and for step-up confirmation.
 */
import { useEffect, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { ApiError, api, setStepUpHandler } from "../../lib/api.ts";
import { getPasskeyAssertion, passkeysSupported } from "../../lib/webauthn.ts";

export interface ChallengeInfo {
  methods: { totp: boolean; recoveryCode: boolean };
  passkeyOptions: Parameters<typeof getPasskeyAssertion>[0] | null;
}

export type Factor =
  | { code: string }
  | { recoveryCode: string }
  | { passkey: Awaited<ReturnType<typeof getPasskeyAssertion>> };

export function SecondFactorForm({
  info,
  onSubmit,
  submitLabel = "Continue",
}: {
  info: ChallengeInfo;
  onSubmit: (factor: Factor) => Promise<void>;
  submitLabel?: string;
}) {
  const [mode, setMode] = useState<"code" | "recovery">(info.methods.totp ? "code" : "recovery");
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function switchMode(next: "code" | "recovery") {
    setMode(next);
    setValue("");
  }

  async function run(factor: () => Promise<Factor>) {
    setBusy(true);
    setError(null);
    try {
      await onSubmit(await factor());
    } catch (e) {
      setError(
        e instanceof ApiError
          ? (Object.values(e.fields)[0] ?? e.message)
          : "That didn't work. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      {error && <Alert variant="danger">{error}</Alert>}
      {info.passkeyOptions && passkeysSupported() && (
        <div className="d-grid mb-3">
          <Button
            variant="primary"
            disabled={busy}
            onClick={() => run(async () => ({ passkey: await getPasskeyAssertion(info.passkeyOptions!) }))}
          >
            Use a passkey
          </Button>
        </div>
      )}
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          run(async () => (mode === "code" ? { code: value.trim() } : { recoveryCode: value.trim() }));
        }}
      >
        <Form.Group className="mb-3" controlId="second-factor">
          <Form.Label>{mode === "code" ? "Code from your authenticator app" : "Recovery code"}</Form.Label>
          <Form.Control
            value={value}
            onChange={(e) => setValue(e.target.value)}
            inputMode={mode === "code" ? "numeric" : "text"}
            autoComplete="one-time-code"
            autoFocus={!info.passkeyOptions}
            placeholder={mode === "code" ? "123456" : "xxxx-xxxx-xxxx"}
          />
        </Form.Group>
        <div className="d-grid mb-2">
          <Button
            type="submit"
            variant={info.passkeyOptions ? "outline-primary" : "primary"}
            disabled={busy || !value.trim()}
          >
            {submitLabel}
          </Button>
        </div>
      </Form>
      <div className="text-center small">
        {mode === "code" ? (
          <Button variant="link" size="sm" onClick={() => switchMode("recovery")}>
            Use a recovery code instead
          </Button>
        ) : (
          info.methods.totp && (
            <Button variant="link" size="sm" onClick={() => switchMode("code")}>
              Use your authenticator app
            </Button>
          )
        )}
      </div>
    </div>
  );
}

/** Registers the step-up handler: when an action needs it, ask for a second factor and retry. */
export function StepUpProvider() {
  const [pending, setPending] = useState<{
    resolve: (ok: boolean) => void;
    info: ChallengeInfo & { stepUpToken: string };
  } | null>(null);

  useEffect(() => {
    setStepUpHandler(async () => {
      const info = await api.post<ChallengeInfo & { stepUpToken: string }>(
        "/api/account/mfa/step-up/options",
      );
      return new Promise<boolean>((resolve) => setPending({ resolve, info }));
    });
    return () => setStepUpHandler(null);
  }, []);

  if (!pending) return null;
  const close = (ok: boolean) => {
    pending.resolve(ok);
    setPending(null);
  };
  return (
    <Modal show centered onHide={() => close(false)}>
      <Modal.Header closeButton>
        <Modal.Title as="h2" className="h5">
          Confirm it's you
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small text-muted">This is a sensitive change, so we need your second factor again.</p>
        <SecondFactorForm
          info={pending.info}
          submitLabel="Confirm"
          onSubmit={async (factor) => {
            await api.post("/api/account/mfa/step-up", { stepUpToken: pending.info.stepUpToken, ...factor });
            close(true);
          }}
        />
      </Modal.Body>
    </Modal>
  );
}
